/**
 * ScriptsList Component - Scripts Performance Dashboard
 * 
 * Displays script performance metrics in a clean table layout
 * with calls, leads, and conversion rate per script.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../shared/Button';
import EmptyState from '../shared/EmptyState';
import LoadingSpinner from '../shared/LoadingSpinner';
import RevisionHistory from '../shared/RevisionHistory';
import { colors, spacing, typography, borderRadius, shadows, zIndex } from '../../styles/designTokens';

/**
 * Time period options
 */
const timePeriodOptions = [
  { value: 'today', label: 'Today' },
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: 'all', label: 'All Time' }
];

/**
 * Get date range for time period
 */
const getDateRange = (period) => {
  const now = new Date();
  const endDate = new Date(now);
  endDate.setHours(23, 59, 59, 999);
  
  let startDate = new Date(now);
  
  switch (period) {
    case 'today':
      startDate.setHours(0, 0, 0, 0);
      break;
    case '7days':
      startDate.setDate(startDate.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      break;
    case '30days':
      startDate.setDate(startDate.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'all':
      return { startDate: null, endDate: null };
    default:
      startDate.setHours(0, 0, 0, 0);
  }
  
  return { startDate, endDate };
};

/**
 * Export data to CSV
 */
const exportToCSV = (data, filename) => {
  const headers = ['Script Name', 'Calls', 'Leads', 'Conversion Rate', 'Drop-offs', 'Top Drop-off Point', 'Is Default', 'Last Updated'];
  
  const rows = data.map(item => {
    const topDropoff = item.topResetPoints && item.topResetPoints.length > 0 
      ? item.topResetPoints[0].nodeId.replace(/^newNode_/, 'Node ').replace(/_/g, ' ')
      : '--';
    
    return [
      `"${item.name.replace(/"/g, '""')}"`,
      item.calls,
      item.leads,
      item.rate > 0 ? `${item.rate}%` : '--',
      item.dropoffs || 0,
      `"${topDropoff}"`,
      item.isDefault ? 'Yes' : 'No',
      item.lastUpdated || ''
    ];
  });
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * ScriptsList Component
 */
const ScriptsList = ({ 
  onEditScript,
  onEditFlowchart,  // NEW: Navigate to flowchart editor (primary edit action)
  onCreateNew, 
  onCreateWithAI,
  onBrowseMarketplace,
  onImproveScript,
  onRestoreRevision,
  defaultTemplateId,
  analytics,
  evaluationCount,
}) => {
  const { templates, isLoadingData } = useData();
  const { userRole } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWorkflowTip, setShowWorkflowTip] = useState(() => {
    // Remember if user dismissed the tip
    return localStorage.getItem('hideWorkflowTip') !== 'true';
  });
  const [timePeriod, setTimePeriod] = useState('today');
  const [scriptMetrics, setScriptMetrics] = useState({});
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);
  
  // Load script performance metrics
  const loadMetrics = useCallback(async () => {
    if (!analytics) return;
    
    setIsLoadingMetrics(true);
    try {
      const { startDate, endDate } = getDateRange(timePeriod);
      const metrics = await analytics.getScriptPerformance(startDate, endDate);
      setScriptMetrics(metrics);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading script metrics:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [analytics, timePeriod]);
  
  // Load metrics on mount and when time period changes
  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);
  
  // Combine templates with metrics and sort by leads
  const scriptsWithMetrics = useMemo(() => {
    const templateArray = Object.values(templates || {});
    
    return templateArray.map(template => {
      const metrics = scriptMetrics[template.id] || { calls: 0, leads: 0, rate: 0, topResetPoints: [] };
      const lastModified = template.metadata?.updatedAt || template.metadata?.createdAt;
      
      // Calculate total drop-offs from topResetPoints
      const totalDropoffs = (metrics.topResetPoints || []).reduce((sum, point) => sum + point.count, 0);
      
      return {
        id: template.id,
        name: template.name || template.id,
        calls: metrics.calls,
        leads: metrics.leads,
        rate: metrics.rate,
        dropoffs: totalDropoffs,
        topResetPoints: metrics.topResetPoints || [],
        isDefault: template.id === defaultTemplateId,
        lastUpdated: lastModified 
          ? new Date(lastModified.seconds ? lastModified.seconds * 1000 : lastModified).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
          })
          : null,
        template
      };
    }).sort((a, b) => {
      // Sort by leads first (descending), then by calls
      if (b.leads !== a.leads) return b.leads - a.leads;
      return b.calls - a.calls;
    });
  }, [templates, scriptMetrics, defaultTemplateId]);
  
  // Handle CSV export
  const handleExport = () => {
    const periodLabel = timePeriodOptions.find(p => p.value === timePeriod)?.label || timePeriod;
    const dateStr = new Date().toISOString().split('T')[0];
    exportToCSV(scriptsWithMetrics, `scripts-performance-${periodLabel.toLowerCase().replace(/\s+/g, '-')}-${dateStr}.csv`);
  };

  // Handle option selection from create modal
  const handleCreateOption = (option) => {
    setShowCreateModal(false);
    switch (option) {
      case 'manual':
        onCreateNew();
        break;
      case 'ai':
        onCreateWithAI();
        break;
      case 'marketplace':
        onBrowseMarketplace();
        break;
      default:
        break;
    }
  };

  // Reusable Create Script Modal
  const CreateScriptModal = () => showCreateModal && (
    <div 
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50
      }}
      onClick={() => setShowCreateModal(false)}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: borderRadius.lg,
          padding: spacing.lg,
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: spacing.lg }}>
          <h2 style={{ 
            margin: 0, 
            fontSize: typography.h2.size, 
            fontWeight: 600,
            color: colors.neutral[900]
          }}>
            Create a New Script
          </h2>
          <p style={{ 
            margin: `${spacing.xs} 0 0 0`, 
            fontSize: typography.small.size, 
            color: colors.neutral[500] 
          }}>
            Choose how you&apos;d like to get started
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          {/* Option 1: Build from Scratch */}
          <button
            onClick={() => handleCreateOption('manual')}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: spacing.md,
              padding: spacing.md,
              backgroundColor: 'white',
              border: `2px solid ${colors.neutral[200]}`,
              borderRadius: borderRadius.lg,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.primary.main;
              e.currentTarget.style.backgroundColor = colors.primary[50];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.neutral[200];
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: borderRadius.md,
              backgroundColor: colors.primary[50],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="24" height="24" fill="none" stroke={colors.primary.main} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: typography.body.size, 
                fontWeight: 600,
                color: colors.neutral[900]
              }}>
                Build from Scratch
              </h3>
              <p style={{ 
                margin: `${spacing.xs} 0 0 0`, 
                fontSize: typography.small.size, 
                color: colors.neutral[500] 
              }}>
                Create a custom script manually using our visual editor. Full control over every question and response.
              </p>
            </div>
            <svg width="20" height="20" fill="none" stroke={colors.neutral[400]} viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '2px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Option 2: Generate with AI */}
          <button
            onClick={() => handleCreateOption('ai')}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: spacing.md,
              padding: spacing.md,
              backgroundColor: 'white',
              border: `2px solid ${colors.neutral[200]}`,
              borderRadius: borderRadius.lg,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.success.main;
              e.currentTarget.style.backgroundColor = colors.success[50];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.neutral[200];
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: borderRadius.md,
              backgroundColor: colors.success[50],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative'
            }}>
              <svg width="24" height="24" fill="none" stroke={colors.success.main} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span style={{
                position: 'absolute',
                top: '-4px',
                right: '-4px',
                backgroundColor: colors.success.main,
                color: 'white',
                fontSize: '8px',
                fontWeight: 700,
                padding: '2px 4px',
                borderRadius: '4px'
              }}>AI</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: typography.body.size, 
                fontWeight: 600,
                color: colors.neutral[900]
              }}>
                Generate with AI
              </h3>
              <p style={{ 
                margin: `${spacing.xs} 0 0 0`, 
                fontSize: typography.small.size, 
                color: colors.neutral[500] 
              }}>
                Upload call recordings and let AI analyze them to generate a script based on your best conversations.
              </p>
            </div>
            <svg width="20" height="20" fill="none" stroke={colors.neutral[400]} viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '2px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Option 3: Browse Marketplace */}
          <button
            onClick={() => handleCreateOption('marketplace')}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: spacing.md,
              padding: spacing.md,
              backgroundColor: 'white',
              border: `2px solid ${colors.neutral[200]}`,
              borderRadius: borderRadius.lg,
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = colors.warning.main;
              e.currentTarget.style.backgroundColor = colors.warning[50];
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.neutral[200];
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: borderRadius.md,
              backgroundColor: colors.warning[50],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <svg width="24" height="24" fill="none" stroke={colors.warning.main} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: typography.body.size, 
                fontWeight: 600,
                color: colors.neutral[900]
              }}>
                Browse Marketplace
              </h3>
              <p style={{ 
                margin: `${spacing.xs} 0 0 0`, 
                fontSize: typography.small.size, 
                color: colors.neutral[500] 
              }}>
                Explore pre-built scripts from other sales teams. Find proven scripts for your industry.
              </p>
            </div>
            <svg width="20" height="20" fill="none" stroke={colors.neutral[400]} viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '2px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div style={{ 
          marginTop: spacing.lg, 
          paddingTop: spacing.md, 
          borderTop: `1px solid ${colors.neutral[200]}`,
          textAlign: 'right'
        }}>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
  
  // Loading state
  if (isLoadingData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: spacing['2xl'] }}>
        <LoadingSpinner message="Loading scripts..." />
      </div>
    );
  }
  
  // Empty state
  if (Object.keys(templates || {}).length === 0) {
    return (
      <>
        <CreateScriptModal />
        <EmptyState
          icon="📜"
          title="No scripts yet"
          description="Create your first call script to get started. You can build one from scratch, let AI generate one, or browse the marketplace."
          action={
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              + Create New Script
            </Button>
          }
        />
      </>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
      {/* Reusable Create Script Modal */}
      <CreateScriptModal />

      {/* Workflow Tip - Collapsible */}
      {showWorkflowTip ? (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: spacing.sm,
          padding: spacing.sm,
          backgroundColor: '#f0f9ff',
          borderRadius: borderRadius.md,
          border: '1px solid #bae6fd'
        }}>
          <svg width="18" height="18" fill="#0ea5e9" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '2px' }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <div style={{ flex: 1, fontSize: typography.small.size, color: '#0c4a6e', lineHeight: 1.4 }}>
            <strong>Tip:</strong> Click <strong>Edit</strong> to build in the Flowchart Editor → Use <strong>⚙️</strong> for advanced settings.
          </div>
          <button
            onClick={() => {
              setShowWorkflowTip(false);
              localStorage.setItem('hideWorkflowTip', 'true');
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#64748b',
              padding: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Dismiss tip"
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
      ) : (
        <button
          onClick={() => {
            setShowWorkflowTip(true);
            localStorage.removeItem('hideWorkflowTip');
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            padding: `${spacing.xs} ${spacing.sm}`,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#0ea5e9',
            fontSize: typography.small.size,
            fontWeight: 500
          }}
          title="Show workflow tip"
        >
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          How to edit scripts
        </button>
      )}

      {/* Create New Script Button */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-start',
        padding: spacing.sm,
        backgroundColor: colors.neutral[50],
        borderRadius: borderRadius.lg,
        border: `1px solid ${colors.neutral[200]}`
      }}>
        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
          + Create New Script
        </Button>
      </div>
      
      {/* Header */}
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: spacing.md
        }}
      >
        <div>
          <h2 style={{ 
            margin: 0, 
            fontSize: typography.h2.size, 
            fontWeight: 600,
            color: colors.neutral[900]
          }}>
            Scripts Performance
          </h2>
          <p style={{ 
            margin: `${spacing.xs} 0 0 0`, 
            fontSize: typography.small.size, 
            color: colors.neutral[500] 
          }}>
            {scriptsWithMetrics.length} {scriptsWithMetrics.length === 1 ? 'script' : 'scripts'}
            {lastUpdated && ` • Last updated ${lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
          {/* Time period selector */}
          <select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              border: `1px solid ${colors.neutral[300]}`,
              borderRadius: borderRadius.md,
              fontSize: typography.small.size,
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
            aria-label="Select time period"
          >
            {timePeriodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          {/* Export button */}
          <Button 
            variant="outline" 
            onClick={handleExport}
            disabled={scriptsWithMetrics.length === 0}
          >
            Export CSV
          </Button>
        </div>
      </div>
      
      {/* Performance table */}
      <div 
        style={{ 
          backgroundColor: 'white',
          borderRadius: borderRadius.lg,
          border: `1px solid ${colors.neutral[200]}`,
          overflow: 'hidden'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: colors.neutral[50] }}>
              <th style={{ 
                padding: `${spacing.sm} ${spacing.md}`,
                textAlign: 'left',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.neutral[600],
                borderBottom: `1px solid ${colors.neutral[200]}`
              }}>
                SCRIPT NAME
              </th>
              <th style={{ 
                padding: `${spacing.sm} ${spacing.md}`,
                textAlign: 'right',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.neutral[600],
                borderBottom: `1px solid ${colors.neutral[200]}`,
                width: '100px'
              }}>
                CALLS
              </th>
              <th style={{ 
                padding: `${spacing.sm} ${spacing.md}`,
                textAlign: 'right',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.neutral[600],
                borderBottom: `1px solid ${colors.neutral[200]}`,
                width: '100px'
              }}>
                LEADS
              </th>
              <th style={{ 
                padding: `${spacing.sm} ${spacing.md}`,
                textAlign: 'right',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.neutral[600],
                borderBottom: `1px solid ${colors.neutral[200]}`,
                width: '100px'
              }}>
                RATE
              </th>
              <th style={{ 
                padding: `${spacing.sm} ${spacing.md}`,
                textAlign: 'right',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.neutral[600],
                borderBottom: `1px solid ${colors.neutral[200]}`,
                width: '120px'
              }}>
                DROP-OFFS
              </th>
              <th style={{ 
                padding: `${spacing.sm} ${spacing.md}`,
                textAlign: 'center',
                fontSize: typography.small.size,
                fontWeight: 600,
                color: colors.neutral[600],
                borderBottom: `1px solid ${colors.neutral[200]}`,
                width: '200px'
              }}>
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoadingMetrics && scriptsWithMetrics.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: spacing.xl, textAlign: 'center' }}>
                  <LoadingSpinner size="small" message="Loading metrics..." />
                </td>
              </tr>
            ) : (
              scriptsWithMetrics.map((script, index) => (
                <tr 
                  key={script.id}
                  style={{ 
                    backgroundColor: selectedScript?.id === script.id
                      ? colors.primary[100] || '#DBEAFE'
                      : index % 2 === 0 ? 'white' : colors.neutral[50],
                    transition: 'background-color 0.15s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedScript(script)}
                  onDoubleClick={() => onEditScript(script.template)}
                  onMouseEnter={(e) => {
                    if (selectedScript?.id !== script.id) {
                      e.currentTarget.style.backgroundColor = colors.primary[50];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedScript?.id !== script.id) {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : colors.neutral[50];
                    }
                  }}
                >
                  <td style={{ 
                    padding: `${spacing.sm} ${spacing.md}`,
                    borderBottom: `1px solid ${colors.neutral[100]}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
                      {script.isDefault && (
                        <span style={{ color: colors.warning.main, fontSize: '14px' }} title="Default Script">★</span>
                      )}
                      <span style={{ 
                        fontWeight: 500, 
                        color: colors.neutral[900],
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '300px'
                      }}>
                        {script.name}
                      </span>
                    </div>
                  </td>
                  <td style={{ 
                    padding: `${spacing.sm} ${spacing.md}`,
                    textAlign: 'right',
                    borderBottom: `1px solid ${colors.neutral[100]}`,
                    fontWeight: 500,
                    color: script.calls > 0 ? colors.neutral[900] : colors.neutral[400]
                  }}>
                    {script.calls}
                  </td>
                  <td style={{ 
                    padding: `${spacing.sm} ${spacing.md}`,
                    textAlign: 'right',
                    borderBottom: `1px solid ${colors.neutral[100]}`,
                    fontWeight: 600,
                    color: script.leads > 0 ? colors.success.dark : colors.neutral[400]
                  }}>
                    {script.leads}
                  </td>
                  <td style={{ 
                    padding: `${spacing.sm} ${spacing.md}`,
                    textAlign: 'right',
                    borderBottom: `1px solid ${colors.neutral[100]}`,
                    fontWeight: 500,
                    color: script.rate > 0 ? colors.neutral[700] : colors.neutral[400]
                  }}>
                    {script.rate > 0 ? `${script.rate}%` : '--'}
                  </td>
                  <td style={{ 
                    padding: `${spacing.sm} ${spacing.md}`,
                    textAlign: 'right',
                    borderBottom: `1px solid ${colors.neutral[100]}`
                  }}>
                    {script.dropoffs > 0 ? (
                      <div 
                        style={{ 
                          position: 'relative',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: script.topResetPoints.length > 0 ? 'help' : 'default'
                        }}
                        title={script.topResetPoints.length > 0 
                          ? `Top drop-off points:\n${script.topResetPoints.slice(0, 3).map(p => 
                              `• ${p.nodeId.replace(/^newNode_/, 'Node ').replace(/_/g, ' ')}: ${p.count}`
                            ).join('\n')}`
                          : undefined
                        }
                      >
                        <span style={{ 
                          fontWeight: 500,
                          color: colors.error.main
                        }}>
                          {script.dropoffs}
                        </span>
                        {script.topResetPoints.length > 0 && (
                          <span style={{ 
                            fontSize: '12px',
                            color: colors.neutral[400]
                          }}>
                            🛑
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: colors.neutral[400] }}>--</span>
                    )}
                  </td>
                  <td style={{ 
                    padding: `${spacing.sm} ${spacing.md}`,
                    textAlign: 'center',
                    borderBottom: `1px solid ${colors.neutral[100]}`
                  }}>
                    <div style={{ display: 'flex', gap: spacing.xs, justifyContent: 'center' }}>
                      {/* Primary: Flowchart Editor */}
                      <button
                        onClick={() => onEditFlowchart ? onEditFlowchart(script) : onEditScript(script.template)}
                        style={{
                          padding: `${spacing.xs} ${spacing.sm}`,
                          fontSize: typography.small.size,
                          fontWeight: 500,
                          color: 'white',
                          backgroundColor: colors.primary.main,
                          border: 'none',
                          borderRadius: borderRadius.md,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = colors.primary.dark;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = colors.primary.main;
                        }}
                        title="Edit in Visual Flowchart Editor"
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                        </svg>
                        Edit
                      </button>
                      
                      {/* AI Improve button */}
                      {onImproveScript && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onImproveScript(script); }}
                          style={{
                            padding: `${spacing.xs} ${spacing.sm}`,
                            fontSize: typography.small.size,
                            fontWeight: 500,
                            color: colors.success.dark,
                            backgroundColor: 'transparent',
                            border: `1px solid ${colors.success.main}40`,
                            borderRadius: borderRadius.md,
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colors.success[50];
                            e.currentTarget.style.borderColor = colors.success.main;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderColor = `${colors.success.main}40`;
                          }}
                          title="Get AI improvement suggestions based on call evaluations"
                        >
                          <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          Improve
                        </button>
                      )}

                      {/* Secondary: Advanced Settings (ScriptBuilder) */}
                      <button
                        onClick={() => onEditScript(script.template)}
                        style={{
                          padding: `${spacing.xs}`,
                          fontSize: typography.small.size,
                          color: colors.neutral[500],
                          backgroundColor: 'transparent',
                          border: `1px solid ${colors.neutral[300]}`,
                          borderRadius: borderRadius.md,
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = colors.neutral[100];
                          e.currentTarget.style.color = colors.neutral[700];
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = colors.neutral[500];
                        }}
                        title="Advanced Settings (objections, response types)"
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        
        {/* No activity message */}
        {!isLoadingMetrics && scriptsWithMetrics.every(s => s.calls === 0) && (
          <div style={{ 
            padding: spacing.lg, 
            textAlign: 'center',
            color: colors.neutral[500],
            fontSize: typography.small.size,
            borderTop: `1px solid ${colors.neutral[100]}`
          }}>
            No call activity for the selected time period
          </div>
        )}
      </div>

      {/* Script Detail Panel */}
      {selectedScript && (
        <ScriptDetailPanel
          script={selectedScript}
          onClose={() => setSelectedScript(null)}
          onEditFlowchart={onEditFlowchart}
          onEditScript={onEditScript}
          defaultTemplateId={defaultTemplateId}
          onRestoreRevision={onRestoreRevision}
        />
      )}
      
    </div>
  );
};

/**
 * ScriptDetailPanel - Slide-out side panel showing script details
 * Mirrors the Leads detail panel pattern.
 */
const ScriptDetailPanel = ({ script, onClose, onEditFlowchart, onEditScript, defaultTemplateId, onRestoreRevision }) => {
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const template = script.template;
  const [activeTab, setActiveTab] = useState('details');
  const { userTeam } = useAuth();

  // Focus the panel on mount for accessibility
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Extract template details
  const graph = template?.graph || template?.data?.scriptGraph || {};
  const nodes = graph?.nodes || {};
  const nodeIds = Object.keys(nodes);
  const totalNodes = nodeIds.length;
  const objectionNodes = nodeIds.filter(id => id.startsWith('objection_') || nodes[id]?.isObjection);
  const regularNodes = nodeIds.filter(id => !id.startsWith('objection_') && !nodes[id]?.isObjection);

  // Find the opening node
  const openingNodeId = nodeIds.find(id => nodes[id]?.isOpeningNode) || 'opening';
  const openingNode = nodes[openingNodeId];

  // Steps/phases
  const steps = template?.steps || [];
  const stepNames = Array.isArray(steps) 
    ? steps.map(s => (typeof s === 'string' ? s : s?.name || s?.id || 'Unknown'))
    : [];

  // Response count
  const totalResponses = nodeIds.reduce((sum, id) => {
    const node = nodes[id];
    const responses = node?.responses || {};
    return sum + Object.keys(responses).length;
  }, 0);

  // Metadata
  const lastModified = template?.metadata?.updatedAt || template?.metadata?.createdAt;
  const lastModifiedStr = lastModified 
    ? new Date(lastModified.seconds ? lastModified.seconds * 1000 : lastModified).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
      })
    : null;

  // Has custom prospect form
  const prospectForm = template?.prospectForm;
  const formFields = prospectForm?.fields || [];

  return (
    <>
      {/* Backdrop overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: zIndex.modalBackdrop
        }}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-label="Script details"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '420px',
          maxWidth: '90vw',
          height: '100vh',
          backgroundColor: '#fff',
          boxShadow: shadows.xl,
          zIndex: zIndex.modal,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none'
        }}
      >
        {/* Header */}
        <div style={{
          padding: spacing.md,
          borderBottom: `1px solid ${colors.neutral[200]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: typography.h3.size, 
              fontWeight: typography.h3.weight,
              color: colors.neutral[900],
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {script.name}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginTop: '4px' }}>
              {script.isDefault && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '2px 8px',
                  borderRadius: '9999px',
                  fontSize: typography.xs.size,
                  fontWeight: 500,
                  color: colors.warning.main,
                  backgroundColor: '#FFFBEB'
                }}>
                  ★ Default
                </span>
              )}
              {lastModifiedStr && (
                <span style={{ fontSize: typography.xs.size, color: colors.neutral[400] }}>
                  Updated {lastModifiedStr}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close detail panel"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: colors.neutral[400],
              padding: spacing.xs,
              borderRadius: borderRadius.sm,
              lineHeight: 1,
              flexShrink: 0
            }}
          >
            ✕
          </button>
        </div>

        {/* Action Buttons */}
        <div style={{
          padding: `${spacing.sm} ${spacing.md}`,
          borderBottom: `1px solid ${colors.neutral[100]}`,
          display: 'flex',
          gap: spacing.sm,
          flexShrink: 0
        }}>
          <button
            onClick={() => { onClose(); onEditFlowchart ? onEditFlowchart(script) : onEditScript(template); }}
            style={{
              flex: 1,
              padding: `${spacing.sm} ${spacing.md}`,
              fontSize: typography.small.size,
              fontWeight: 600,
              color: 'white',
              backgroundColor: colors.primary.main,
              border: 'none',
              borderRadius: borderRadius.md,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.xs,
              transition: 'background-color 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.primary.dark; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primary.main; }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
            </svg>
            Open Flowchart Editor
          </button>
          <button
            onClick={() => { onClose(); onEditScript(template); }}
            style={{
              padding: `${spacing.sm} ${spacing.md}`,
              fontSize: typography.small.size,
              fontWeight: 500,
              color: colors.neutral[600],
              backgroundColor: 'white',
              border: `1px solid ${colors.neutral[300]}`,
              borderRadius: borderRadius.md,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.xs,
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.neutral[50]; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </button>
        </div>

        {/* Tab bar */}
        <div style={{
          display: 'flex',
          borderBottom: `1px solid ${colors.neutral[200]}`,
          flexShrink: 0,
          backgroundColor: 'white',
        }}>
          {['details', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: `${spacing.sm} ${spacing.md}`,
                fontSize: typography.small.size,
                fontWeight: activeTab === tab ? 600 : 400,
                color: activeTab === tab ? colors.primary.main : colors.neutral[500],
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab ? `2px solid ${colors.primary.main}` : '2px solid transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                textTransform: 'capitalize',
              }}
            >
              {tab === 'history' ? '📜 History' : '📋 Details'}
            </button>
          ))}
        </div>

        {/* History Tab */}
        {activeTab === 'history' && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <RevisionHistory
              key={template?.id}
              teamId={userTeam}
              templateId={template?.id}
              templateName={script.name}
              currentGraph={template?.graph || {}}
              onRestore={onRestoreRevision
                ? (revision) => onRestoreRevision(revision, template?.id)
                : undefined}
            />
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: spacing.md }}>
          {/* Summary Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: spacing.sm,
            marginBottom: spacing.lg
          }}>
            <StatBox label="Questions" value={regularNodes.length} color={colors.primary.main} />
            <StatBox label="Responses" value={totalResponses} color={colors.success?.main || '#10B981'} />
            <StatBox label="Objections" value={objectionNodes.length} color={colors.warning?.main || '#F59E0B'} />
          </div>

          {/* Performance (from metrics) */}
          <SectionHeader title="Performance" />
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: spacing.sm,
            marginBottom: spacing.lg
          }}>
            <StatBox label="Calls" value={script.calls || 0} color={colors.neutral[600]} />
            <StatBox label="Leads" value={script.leads || 0} color={colors.success?.dark || '#059669'} />
            <StatBox 
              label="Conv. Rate" 
              value={script.rate > 0 ? `${script.rate}%` : '--'} 
              color={script.rate > 0 ? colors.neutral[700] : colors.neutral[400]} 
            />
          </div>

          {/* Drop-offs */}
          {script.dropoffs > 0 && (
            <div style={{ marginBottom: spacing.lg }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                padding: spacing.sm,
                backgroundColor: '#FEF2F2',
                borderRadius: borderRadius.md,
                border: '1px solid #FECACA'
              }}>
                <span style={{ fontSize: '1rem' }}>🛑</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: typography.small.size, fontWeight: 600, color: '#991B1B' }}>
                    {script.dropoffs} drop-off{script.dropoffs !== 1 ? 's' : ''}
                  </div>
                  {script.topResetPoints && script.topResetPoints.length > 0 && (
                    <div style={{ fontSize: typography.xs.size, color: '#B91C1C', marginTop: '2px' }}>
                      Top: {script.topResetPoints.slice(0, 2).map(p => 
                        p.nodeId.replace(/^newNode_/, 'Node ').replace(/_/g, ' ')
                      ).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Phases/Steps */}
          {stepNames.length > 0 && (
            <>
              <SectionHeader title="Phases" />
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: spacing.xs, 
                marginBottom: spacing.lg 
              }}>
                {stepNames.map((name, i) => (
                  <span 
                    key={i}
                    style={{
                      padding: '3px 10px',
                      borderRadius: '9999px',
                      fontSize: typography.xs.size,
                      fontWeight: 500,
                      backgroundColor: colors.primary[50],
                      color: colors.primary.dark || colors.primary.main,
                      border: `1px solid ${colors.primary.light || colors.primary.main}20`
                    }}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </>
          )}

          {/* Opening Node Preview */}
          {openingNode && (
            <>
              <SectionHeader title="Opening Script" />
              <div style={{
                padding: spacing.sm,
                backgroundColor: colors.neutral[50],
                borderRadius: borderRadius.md,
                border: `1px solid ${colors.neutral[200]}`,
                marginBottom: spacing.lg,
                fontSize: typography.small.size,
                color: colors.neutral[700],
                lineHeight: 1.5,
                maxHeight: '120px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {openingNode.script || 'No script text'}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '32px',
                  background: `linear-gradient(transparent, ${colors.neutral[50]})`,
                  pointerEvents: 'none'
                }} />
              </div>
            </>
          )}

          {/* Node List (top 10) */}
          <SectionHeader title={`Questions (${regularNodes.length})`} />
          <div style={{ marginBottom: spacing.lg }}>
            {regularNodes.slice(0, 10).map((nodeId) => {
              const node = nodes[nodeId];
              const responseCount = Object.keys(node?.responses || {}).length;
              const scriptPreview = (node?.script || '').substring(0, 60);
              return (
                <div 
                  key={nodeId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                    padding: `${spacing.xs} ${spacing.sm}`,
                    borderBottom: `1px solid ${colors.neutral[100]}`,
                    fontSize: typography.small.size
                  }}
                >
                  <span style={{ 
                    fontWeight: 500, 
                    color: node?.isOpeningNode ? colors.primary.main : colors.neutral[700],
                    minWidth: '80px',
                    flexShrink: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {node?.isOpeningNode && '★ '}{nodeId}
                  </span>
                  <span style={{ 
                    flex: 1, 
                    color: colors.neutral[500],
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: typography.xs.size
                  }}>
                    {scriptPreview || '—'}...
                  </span>
                  <span style={{
                    flexShrink: 0,
                    padding: '1px 6px',
                    borderRadius: '9999px',
                    fontSize: '10px',
                    fontWeight: 500,
                    backgroundColor: colors.neutral[100],
                    color: colors.neutral[500]
                  }}>
                    {responseCount} resp
                  </span>
                </div>
              );
            })}
            {regularNodes.length > 10 && (
              <div style={{ 
                padding: spacing.sm, 
                textAlign: 'center',
                fontSize: typography.xs.size,
                color: colors.neutral[400]
              }}>
                +{regularNodes.length - 10} more questions
              </div>
            )}
          </div>

          {/* Lead Capture Form link */}
          <SectionHeader title="Lead Capture Form" />
          <div style={{ marginBottom: spacing.lg }}>
            <button
              onClick={() => {
                onClose();
                navigate('/leads', { state: { formScriptId: template?.id || 'default' } });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                width: '100%',
                padding: `${spacing.sm} ${spacing.md}`,
                fontSize: typography.small.size,
                fontWeight: 500,
                color: colors.primary.main,
                backgroundColor: colors.neutral[50],
                border: `1px solid ${colors.neutral[200]}`,
                borderRadius: borderRadius.md,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#EFF6FF';
                e.currentTarget.style.borderColor = colors.primary.light;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.neutral[50];
                e.currentTarget.style.borderColor = colors.neutral[200];
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {formFields.length > 0
                ? `Custom form · ${formFields.length} field${formFields.length !== 1 ? 's' : ''}`
                : 'Using team default form'}
              <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginLeft: 'auto', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        )}
      </div>
    </>
  );
};

/**
 * StatBox - Small stat display for the detail panel grid
 */
const StatBox = ({ label, value, color }) => (
  <div style={{
    textAlign: 'center',
    padding: spacing.sm,
    backgroundColor: colors.neutral[50],
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.neutral[200]}`
  }}>
    <div style={{ 
      fontSize: typography.h3?.size || '1.125rem', 
      fontWeight: 700, 
      color: color || colors.neutral[900] 
    }}>
      {value}
    </div>
    <div style={{ 
      fontSize: typography.xs.size, 
      color: colors.neutral[500], 
      marginTop: '2px' 
    }}>
      {label}
    </div>
  </div>
);

/**
 * SectionHeader - Consistent section divider in the detail panel
 */
const SectionHeader = ({ title }) => (
  <h4 style={{ 
    fontSize: typography.small.size, 
    fontWeight: 600, 
    color: colors.neutral[500],
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: `0 0 ${spacing.sm} 0`
  }}>
    {title}
  </h4>
);

export default ScriptsList;
